import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  A LOADING PLACEHOLDER. [P6a]
 *
 *  `web/` has never had one: the two idioms are AntD's `<Spin />` for whole-page gating and
 *  `loading` on a button. Neither works for a grid of tiles that arrive together and must not
 *  reflow — a spinner where a number will be says nothing about how much room it needs.
 *
 *  ⚠️ `motion-reduce:animate-none`, NEVER A DURATION TOKEN. `styles/index.css` carries
 *  `--pams-chrome` and no motion token at all, so the hazard here is INVENTING one: a keyframe
 *  animation does not read a transition duration, and a reader who asked for no motion would
 *  get a full-speed pulse from a variable that looked like it governed it. The variant is the
 *  mechanism that actually applies.
 *
 *  ⚠️ AND THE ANNOUNCEMENT SURVIVES. Replacing visible text with bars silently removes what a
 *  screen reader had, so `Loading` carries `role="status"` with an `sr-only` sentence and the
 *  bars are `aria-hidden`. `pwa/src/components/Skeleton.tsx` is the model and its two rules
 *  transfer unchanged.
 *
 *  ⚠️ THE LABEL IS A PROP, NEVER IMPORTED COPY. `components/` must not reach into a page's
 *  `copy.ts` — `CoverageBar`'s `hint` sets that precedent.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** One bar. Width and height come from the caller, because the SHAPE is the message. */
export const Skeleton = ({ className }: { className?: string }) => (
  <span
    aria-hidden="true"
    data-testid="skeleton"
    className={cn('block animate-pulse rounded-md bg-active motion-reduce:animate-none', className)}
  />
);

/**
 * The wrapper every skeleton goes in.
 *
 * ⚠️ `relative`, AND IT IS LOAD-BEARING. `sr-only` is `position: absolute` with no offsets, so
 * with no positioned ancestor its containing block is the INITIAL one — and an absolutely
 * positioned box whose containing block is the ICB is clipped by no `overflow: hidden` between
 * it and the root. [D4] measured that: a live region below the fold grew
 * `documentElement.scrollHeight` by 531px and broke "the window never scrolls" with an
 * invisible paragraph that only a browser could see.
 *
 * `className` is for a placeholder that must FILL a slot rather than size itself — a flex column
 * whose bar is `flex-1` ([LOCATION-INTEL.1]). Layout classes only: a position class would replace
 * the load-bearing `relative` (`cn` merges by property).
 */
export const Loading = ({ label, children, className }: { label: string; children: ReactNode; className?: string }) => (
  <div role="status" aria-busy="true" data-testid="loading" className={cn('relative', className)}>
    <span className="sr-only">{label}</span>
    {children}
  </div>
);
